import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowRight, CalendarCheck, Flame, Menu, ShieldCheck, Star, Users, X } from "lucide-react";
import { WhatsAppButton } from "./HomeShared";
import { useAppointment } from "../../context/AppointmentContext";
import { NAV_LINKS, WHATSAPP_LINK } from "../../lib/constants";
import heroImage from "../../assets/smileimg.jpeg";
import flagImage from "../../assets/flag.svg";
import logo from "../../assets/headerlogo.png";
import whatsappIcon from "../../assets/whatsapplogo.svg";
import userIcon from "../../assets/bi_people.svg";

function OfferCard() {
  const { openAppointment } = useAppointment();

  return (
    <aside className="min-w-0 max-w-full overflow-hidden rounded-[20px] bg-[#06386f] px-9 pb-9 pt-9 text-white shadow-[0_24px_50px_rgba(3,24,55,0.22)] md:h-[489px] md:w-[401px]">
      <span className="inline-flex h-8 items-center gap-2 rounded-[5px] bg-[#ffc928] px-3 text-[12px] font-extrabold uppercase text-white">
        <Flame className="h-4 w-4 fill-[#ff7a00] text-[#ff7a00]" />
        Limited Offer
      </span>
      <h2 className="mt-[31px] text-[34px] font-medium leading-none tracking-normal md:text-[35px]">Teeth Whitening</h2>
      <p className="mt-[11px] max-w-[300px] text-[26px] font-normal leading-[1.38] tracking-normal">
        Up to 8 shades<br />brighter in 1 visit!
      </p>
      <p className="mt-[13px] text-[18px] font-normal leading-none">Now Only</p>
      <div className="mt-[11px] flex items-end gap-[14px]">
        <strong className="text-[56px] font-semibold leading-[0.85] tracking-normal">$15,000</strong>
        <span className="pb-[3px] text-[20px] font-bold leading-none">JMD</span>
      </div>
      <button
        type="button"
        onClick={openAppointment}
        className="mx-auto mt-[28px] flex h-[52px] w-[245px] items-center justify-center rounded-[20px] bg-[#079b65] px-6 text-[23px] font-medium leading-none text-white transition hover:bg-[#087e54]"
      >
        Claim Offer Now
      </button>
      <p className="mt-[13px] text-center text-[14px] leading-none text-white/90">Offer valid for a limited time only.</p>
    </aside>
  );
}

function HeroStats() {
  return (
    <div className="mt-7 grid gap-4 text-[#071746] sm:grid-cols-3 md:max-w-[650px] md:divide-x md:divide-[#b9bec8]">
      <div>
        <div className="flex gap-2 text-[#ffb800]">
          {[...Array(5)].map((_, index) => (
            <Star key={index} className="h-5 w-5 fill-current" />
          ))}
        </div>
        <p className="mt-1 text-[12px] font-medium">Rated 4.9 by 300+ patients</p>
      </div>
      <div className="flex items-center gap-3 md:pl-8">
        <img src={userIcon} alt="Users" className="h-7 w-7" />
        <div>
          <p className="text-[15px] font-semibold">Over 5,000</p>
          <p className="text-[12px]">Smiles transformed</p>
        </div>
      </div>
      <div className="flex items-center gap-3 md:pl-8">
        <ShieldCheck className="h-8 w-8" />
        <div>
          <p className="text-[15px] font-semibold">20+ years</p>
          <p className="text-[12px]">of clinical excellence</p>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { openAppointment } = useAppointment();
  const [menuOpen, setMenuOpen] = useState(false);

  const openBooking = () => {
    setMenuOpen(false);
    openAppointment();
  };

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 bg-white shadow-sm lg:hidden">
        <div className="flex h-[70px] items-center justify-between px-4">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="grid h-11 w-11 place-items-center rounded-full text-[#071746] transition hover:bg-[#eef4ff]"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-2">
            <img src={logo} alt="Be Dental Paradise" className="h-10 w-10" />
            <span className="text-[15px] font-bold leading-tight">Be Dental<br />Paradise</span>
          </Link>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="grid h-10 w-10 place-items-center rounded-full bg-[#079b65] transition hover:bg-[#087e54]"
            aria-label="Book on WhatsApp"
          >
            <img src={whatsappIcon} alt="" className="block h-5 w-5" />
          </a>
        </div>
        {menuOpen && (
          <div className="border-t border-[#e8edf7] bg-white px-4 pb-5 pt-3 shadow-[0_18px_30px_rgba(7,23,70,0.08)]">
            <nav className="grid gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `rounded-[8px] px-3 py-3 text-[16px] font-bold transition ${
                      isActive ? "bg-[#eef4ff] text-[#0052ff]" : "text-[#071746] hover:bg-[#f5f8ff]"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={openBooking}
                className="mt-2 inline-flex h-12 items-center justify-center rounded-[10px] bg-[#0052ff] px-4 text-[16px] font-bold text-white"
              >
                Book Appointment
              </button>
            </nav>
          </div>
        )}
      </div>

      <section className="relative mt-[70px] min-h-[690px] max-w-full overflow-hidden bg-white lg:mt-[156px] lg:min-h-[660px]">
        <img src={heroImage} alt="Close-up of a bright dental smile" className="absolute inset-0 h-full w-full object-cover object-[62%_center] lg:left-[24.2vw] lg:right-0 lg:w-[75.8vw] lg:object-center" />
        <div className="absolute inset-0 bg-white/70 lg:bg-white/0" />
        <div className="absolute inset-y-0 left-0 w-[60%] bg-gradient-to-r from-white via-white/95 to-transparent" />
        <div className="relative z-10 grid w-full min-w-0 gap-8 overflow-hidden px-5 py-10 lg:grid-cols-[minmax(0,1fr)_401px] lg:items-center lg:px-10 xl:px-[130px] xl:py-12">
          <div className="min-w-0">
            <div className="inline-flex h-9 max-w-full items-center overflow-hidden rounded-full bg-[#99a4bb] pr-4 text-[11px] font-bold uppercase text-white sm:text-[12px]">
              <img src={flagImage} alt="Jamaica flag" className="mr-3 h-9 w-[64px] rounded-full object-cover" />
              Jamaica's Trusted Dental Care
            </div>
            <h1 className="mt-8 w-full max-w-[calc(100vw-40px)] text-[34px] font-bold leading-[1.12] text-[#071746] [overflow-wrap:anywhere] min-[390px]:text-[36px] sm:max-w-[820px] sm:text-[48px] md:w-auto md:text-[62px] xl:text-[68px] 2xl:max-w-[910px]">
              Your smile is the first thing people notice.
              <span className="block text-[#0052ff]">Make it unforgettable.</span>
            </h1>
            <p className="mt-6 w-full max-w-[calc(100vw-40px)] text-[17px] leading-snug text-black [overflow-wrap:anywhere] sm:max-w-[650px] md:w-auto md:text-[20px] 2xl:max-w-[720px]">
              Same-day smile solutions, cosmetic restorations, and pain relief - <span className="text-[#0052ff]">trusted for over 10 years in Jamaica.</span>
            </p>
            <div className="mt-8 flex w-full max-w-[650px] flex-col gap-4 lg:max-w-none lg:flex-row">
              <button
                type="button"
                onClick={openAppointment}
                className="inline-flex h-[58px] w-full max-w-[650px] items-center justify-center gap-2 rounded-[10px] bg-[#0052ff] px-5 text-center text-[16px] font-bold leading-tight text-white shadow-[0_16px_34px_rgba(0,82,255,0.24)] transition hover:bg-[#003ec2] lg:h-auto lg:w-auto lg:max-w-none lg:px-7 lg:py-3.5 lg:text-[17px]"
              >
                <CalendarCheck className="h-5 w-5 shrink-0" />
                Book Appointment
              </button>
              <WhatsAppButton className="h-[58px] w-full max-w-[650px] gap-3 px-5 py-0 text-center text-[16px] leading-tight lg:h-auto lg:w-auto lg:max-w-none lg:px-7 lg:py-3.5 lg:text-[17px]" iconClassName="h-6 w-6 shrink-0">
                <span>
                  <span className="block whitespace-nowrap font-semibold text-left">Chat on WhatsApp Now</span>
                  <span className="block text-[10px] font-normal lg:text-[12px] text-left">Response in under 10 minutes</span>
                </span>
              </WhatsAppButton>
              <a href="#before-after" className="inline-flex h-[58px] w-full max-w-[650px] items-center justify-center gap-3 rounded-[10px] border border-[#aeb3bd] bg-white/10 px-6 text-center text-[15px] font-semibold leading-tight text-black lg:h-auto lg:w-auto lg:max-w-none lg:px-8 lg:py-3.5">
                See Before &amp; After <ArrowRight className="h-5 w-5 shrink-0" />
              </a>
            </div>
            <HeroStats />
          </div>
          <div className="min-w-0 lg:translate-y-4">
            <OfferCard />
          </div>
        </div>
      </section>
    </>
  );
}
